<template>
  <div class="reply-box" :class="replyBoxClass">
    <banner
      v-if="showSelfAssignBanner"
      action-button-variant="link"
      color-scheme="secondary"
      :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
      :has-action-button="true"
      :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
      @click="onClickSelfAssign"
    />
    <reply-top-panel
      :mode="replyType"
      :set-reply-mode="setReplyMode"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popoutReplyBox"
      :is-private-note-enabled="isPrivateNoteEnabled"
      @click="$emit('click')"
    />

    <div class="composer-box">
    <div class="reply-box__top">
      <div v-if="replyEnabled" class="replyContainer">
        <div :class="showDiv">
          <p v-if="isGroupMessage" class="title">
            {{ toBeReplied.additional_attributes.participant.name }}
          </p>
          <p v-if="!isGroupMessage" class="title">{{ toBeReplied.name }}</p>
          <div v-if="repliedAttachment" class="replied-message__media">
            <img
              v-if="repliedAttachment.file_type === 'image'"
              :src="repliedAttachment.thumb_url || repliedAttachment.data_url"
              class="replied-message__thumb"
            />
            <video
              v-else
              :src="repliedAttachment.data_url"
              class="replied-message__thumb"
              preload="metadata"
              muted
            />
            <span class="replied-message__media-label">
              <fluent-icon :icon="repliedAttachment.file_type" size="14" />
              {{
                $t(`CONVERSATION.${repliedAttachment.file_type.toUpperCase()}`)
              }}
            </span>
          </div>
          <p v-if="toBeReplied.content">{{ toBeReplied.content }}</p>
        </div>
        <div>
          <p class="cancel-button" @click="$emit('external-id', {})">
            {{ $t('CONVERSATION.FOOTER.REPLY_CANCEL') }}
          </p>
        </div>
      </div>
      <canned-response
        v-if="showMentions && hasSlashCommand"
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @click="replaceText"
        @tag-id="tagConversation"
        @media-url="addMedia"
      />
      <emoji-input
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :class="emojiDialogClassOnExpandedLayoutAndRTLView"
        :on-click="emojiOnClick"
      />
      <reply-email-head
        v-if="showReplyHead"
        :cc-emails.sync="ccEmails"
        :bcc-emails.sync="bccEmails"
      />
      <woot-audio-recorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @state-recorder-progress-changed="onStateProgressRecorderChanged"
        @state-recorder-changed="onStateRecorderChanged"
        @finish-record="onFinishRecorder"
      />
      <resizable-text-area
        v-else-if="!showRichContentEditor"
        ref="messageInput"
        v-model="message"
        class="input"
        :placeholder="messagePlaceHolder"
        :min-height="4"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
      />
      <woot-message-editor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        :enable-variables="true"
        :variables="messageVariables"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
        @tag-id="tagConversation"
        @media-url="addMedia"
      />
    </div>
    <div v-if="isUploading" class="loading-indicator">
      <p>Carregando arquivo, por favor aguarde...</p>
    </div>
    <div v-if="hasAttachments" class="attachment-preview-box" @paste="onPaste">
      <attachment-preview
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
      />
      <attachment-location
        :attachment="attachedLocation"
        :remove-attachment="removeAttachmentLocation"
      />
    </div>
    <div
      v-if="isSignatureEnabledForInbox"
      v-tooltip="$t('CONVERSATION.FOOTER.MESSAGE_SIGN_TOOLTIP')"
      class="message-signature-wrap"
    >
      <p
        v-if="isSignatureAvailable"
        v-dompurify-html="formatMessage(messageSignature)"
        class="message-signature"
      />
      <p v-else class="message-signature">
        {{ $t('CONVERSATION.FOOTER.MESSAGE_SIGNATURE_NOT_CONFIGURED') }}
        <router-link :to="profilePath">
          {{ $t('CONVERSATION.FOOTER.CLICK_HERE') }}
        </router-link>
      </p>
    </div>
    <reply-bottom-panel
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :has-whatsapp-templates="hasWhatsappTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :mode="replyType"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-location="toggleLocation"
      :toggle-contact="toggleContact"
      :toggle-emoji-picker="toggleEmojiPicker"
      :message="message"
      @selectWhatsappTemplate="openWhatsappTemplateModal"
      @toggle-editor="toggleRichContentEditor"
      @replace-text="replaceText"
      @send-message-product="onSendProduct"
    />
    </div>
    <whatsapp-templates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      :contact-id="currentChat.contact_id"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';
import adminMixin from '../../../mixins/isAdmin';

import {
  debounce,
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
  trimContent,
} from '@chatwoot/utils';
import { DirectUpload } from 'activestorage';
import {
  buildConversationDirectUploadUrl,
  buildBlobUrl,
} from 'dashboard/helper/directUploadHelper';
import Banner from 'dashboard/components/ui/Banner.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import WootAudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import conversationLabelMixin from 'dashboard/mixins/conversation/labelMixin';
import uiSettingsMixin, {
  isEditorHotKeyEnabled,
} from 'dashboard/mixins/uiSettings';
import ResizableTextArea from 'shared/components/ResizableTextArea';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  AUDIO_FORMATS,
  MAXIMUM_FILE_UPLOAD_SIZE,
  MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL,
} from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { buildHotKeys } from 'shared/helpers/KeyboardHelpers';
import { LocalStorage } from 'shared/helpers/localStorage';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin from 'shared/mixins/inboxMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { frontendURL } from '../../../helper/URLHelper';
import { extractEmailLinks } from '../../../helper/validateLink';
import AttachmentLocation from '../AttachmentLocation.vue';
import CannedResponse from './CannedResponse';
import ReplyEmailHead from './ReplyEmailHead';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import { heicTo } from 'heic-to';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

export default {
  components: {
    EmojiInput,
    CannedResponse,
    ResizableTextArea,
    AttachmentPreview,
    AttachmentLocation,
    ReplyTopPanel,
    ReplyEmailHead,
    ReplyBottomPanel,
    WootMessageEditor,
    WootAudioRecorder,
    Banner,
    WhatsappTemplates,
  },
  mixins: [
    clickaway,
    inboxMixin,
    uiSettingsMixin,
    conversationLabelMixin,
    alertMixin,
    messageFormatterMixin,
    rtlMixin,
    adminMixin,
  ],
  props: {
    selectedTweet: {
      type: [Object, String],
      default: () => ({}),
    },
    toBeReplied: {
      type: [Object, String],
      default: '',
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      labelToInsert: null,
      message: '',
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      attachedLocation: undefined,
      attachedContact: undefined,
      repliedMessage: null,
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      ccEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      labelList: 'labels/getLabels',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      activeInbox: 'getSelectedInbox',
    }),
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    inbox() {
      const { inbox_id: inboxId } = this.currentChat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    // inbox() {
    //   return this.$store.getters['inboxes/getInbox'](this.inboxId);
    // },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    repliedAttachment() {
      return this.toBeReplied?.attachment || null;
    },
    replyEnabled() {
      return !!this.toBeReplied?.content || !!this.repliedAttachment;
    },
    showDiv() {
      if (this.replyEnabled) {
        return 'replied-message';
      }
      return '';
    },
    isGroupMessage() {
      return !!this.toBeReplied?.additional_attributes?.participant;
    },
    showSelfAssignBanner() {
      if (this.message !== '' && !this.isOnPrivateNote) {
        if (!this.assignedAgent) {
          return true;
        }
        if (
          this.assignedAgent.id !== this.currentUser.id &&
          this.inbox.enable_fowarding_conversations
        ) {
          return true;
        }
      }
      return false;
    },
    hasWhatsappTemplates() {
      return !!this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
        .length;
    },
    isPrivate() {
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },

    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (
        (this.isATweet && !this.inReplyTo && !this.isOnPrivateNote) ||
        this.isUploading
      ) {
        return true;
      }

      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isATwitterInbox) {
        if (this.conversationType === 'tweet') {
          return MESSAGE_MAX_LENGTH.TWEET - this.replyToUserLength - 2;
        }
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      } else if (this.conversationType === 'tweet') {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.TWEET');
      }
      const keyLabel = isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return (
        this.attachedFiles.length ||
        this.attachedLocation ||
        this.attachedContact
      );
    },
    hasRecordedAudio() {
      return (
        this.$refs.audioRecorderInput &&
        this.$refs.audioRecorderInput.hasAudio()
      );
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    inReplyTo() {
      const selectedTweet = this.selectedTweet || {};
      return selectedTweet.id;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const {
        conversation_display_type: conversationDisplayType = CONDENSED,
      } = this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    emojiDialogClassOnExpandedLayoutAndRTLView() {
      if (this.isOnExpandedLayout || this.popoutReplyBox) {
        return 'emoji-dialog--expanded';
      }
      if (this.isRTLView) {
        return 'emoji-dialog--rtl';
      }
      return '';
    },
    replyToUserLength() {
      const selectedTweet = this.selectedTweet || {};
      const {
        sender: {
          additional_attributes: { screen_name: screenName = '' } = {},
        } = {},
      } = selectedTweet;
      return screenName ? screenName.length : 0;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.isAnEmailChannel && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      const { send_with_signature: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    profilePath() {
      return frontendURL(`accounts/${this.accountId}/profile/settings`);
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isAPIInbox) {
        return AUDIO_FORMATS.OGG;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
      });
      return variables;
    },
    isPrivateNoteEnabled() {
      return (
        this.inbox?.enable_private_notes &&
        this.currentUser?.permissions?.see_private_message
      );
    },
  },
  watch: {
    toBeReplied() {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = '\u200B';
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        this.$refs.messageInput.focus();
      }
    },
    currentChat(conversation) {
      const { can_reply: canReply } = conversation;

      if (this.isOnPrivateNote) {
        return;
      }
      if (canReply || this.isAWhatsAppChannel) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.setCCEmailFromLastChat();
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      this.cleanAttachedFiles();
      this.isRecordingAudio = false;
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
      }
    },
    message(updatedMessage) {
      this.hasSlashCommand =
        updatedMessage[0] === '/' && !this.showRichContentEditor;
      const hasNextWord = updatedMessage.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.mentionSearchKey = updatedMessage.substring(1);
        this.showMentions = true;
      } else {
        this.mentionSearchKey = '';
        this.showMentions = false;
      }
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // Donot use the keyboard listener mixin here as the events here are supposed to be
    // working even if input/textarea is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    // Copiloto IA: sugestões da aba lateral inserem texto no compositor.
    bus.$on(BUS_EVENTS.SET_REPLY_EDITOR_MESSAGE, this.replaceText);
    this.setCCEmailFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );
  },
  destroyed() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    bus.$off(BUS_EVENTS.SET_REPLY_EDITOR_MESSAGE, this.replaceText);
  },
  methods: {
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });
    },
    getSavedDraftMessages() {
      return LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES) || {};
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const savedDraftMessages = this.getSavedDraftMessages();
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');
        const {
          [key]: currentDraft,
          ...restOfDraftMessages
        } = savedDraftMessages;

        const updatedDraftMessages = draftToSave
          ? {
              ...restOfDraftMessages,
              [key]: draftToSave,
            }
          : restOfDraftMessages;

        LocalStorage.set(
          LOCAL_STORAGE_KEYS.DRAFT_MESSAGES,
          updatedDraftMessages
        );
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        try {
          const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
          const savedDraftMessages = this.getSavedDraftMessages();
          this.message = `${savedDraftMessages[key] || ''}`;
        } catch (error) {
          this.message = '';
        }
      }
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const draftMessages = this.getSavedDraftMessages();
        const { [key]: toBeRemoved, ...updatedDraftMessages } = draftMessages;
        LocalStorage.set(
          LOCAL_STORAGE_KEYS.DRAFT_MESSAGES,
          updatedDraftMessages
        );
      }
    },
    handleKeyEvents(e) {
      const keyCode = buildHotKeys(e);
      if (keyCode === 'escape') {
        this.hideEmojiPicker();
        this.hideMentions();
      } else if (keyCode === 'meta+k') {
        const ninja = document.querySelector('ninja-keys');
        ninja.open();
        e.preventDefault();
      } else if (keyCode === 'enter' && this.isAValidEvent('enter')) {
        this.onSendReply();
        e.preventDefault();
      } else if (
        ['meta+enter', 'ctrl+enter'].includes(keyCode) &&
        this.isAValidEvent('cmd_enter')
      ) {
        this.onSendReply();
      }
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        isEditorHotKeyEnabled(this.uiSettings, selectedKey)
      );
    },
    onPaste(e) {
      const data = Array.from(e.clipboardData.files);

      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }
      if (data) {
        data.forEach(file => {
          const fileObject = {
            file,
            name: file.name,
            type: file.type,
            size: file.size,
          };
          this.onFileUpload(fileObject);
        });
      }
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    onClickSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        let newMessage = this.message;
        if (this.isSignatureEnabledForInbox && this.messageSignature) {
          newMessage += '\n\n' + this.messageSignature;
        }

        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        if (isOnWhatsApp && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(newMessage);
        } else {
          const messagePayload = this.getMessagePayload(newMessage);

          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }
        this.$emit('external-id', {});
        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popoutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMessagePayloadForWhatsapp(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    whatsappFormatter(text) {
      const {
        ITALIC_PATTERN,
        ITALIC_CHARACTER,
        BOLD_PATTERN,
        BOLD_CHARACTER,
        ITALIC_BOLD_PATTERN,
        ITALIC_BOLD_CHARACTER,
        ITALIC_BOLD_CHARACTER_REVERSE,
      } = wootConstants.WHATSAPP_FORMATTER;

      text = text.replace(
        ITALIC_PATTERN,
        `${ITALIC_CHARACTER}$1${ITALIC_CHARACTER}`
      );
      text = text.replace(BOLD_PATTERN, `${BOLD_CHARACTER}$1${BOLD_CHARACTER}`);
      text = text.replace(
        ITALIC_BOLD_PATTERN,
        `${ITALIC_BOLD_CHARACTER}$1${ITALIC_BOLD_CHARACTER_REVERSE}`
      );
      return text;
    },
    async sendMessage(messagePayload) {
      if (this.labelToInsert) {
        this.addLabelToConversation(this.labelToInsert);
      }
      if (
        this.showRichContentEditor &&
        this.currentChat?.meta?.channel === 'Channel::Api'
      ) {
        messagePayload.message = this.whatsappFormatter(messagePayload.message);
      }

      if (this.currentUser?.message_signature && messagePayload?.message) {
        messagePayload.message = `${
          this.currentChat?.meta?.channel === 'Channel::Whatsapp' ||
          this.currentChat?.meta?.channel === 'Channel::Api'
            ? this.whatsappFormatter(this.currentUser.message_signature)
            : this.currentUser.message_signature
        } \n\n ${messagePayload.message}`;
      }

      // TODO: Remove that line if whatsapp starts to support markdown links , this method replaces (email@mail.com)[mailto: email@mail.com] to -> only email@mail.com
      if (
        this.currentChat?.meta?.channel === 'Channel::Api' ||
        this.currentChat?.meta?.channel === 'Channel::Whatsapp'
      ) {
        messagePayload.message = extractEmailLinks(messagePayload.message);
      }

      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        this.removeFromDraft();
        this.labelToInsert = null;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    // eslint-disable-next-line consistent-return
    async urlToFile(url) {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      try {
        // const requestValor = await integrations.searchFileByUrl(url)
        const blob = response.data;

        const now = new Date();

        const formattedDate = `${now.getFullYear()}-${(now.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${now
          .getDate()
          .toString()
          .padStart(2, '0')}_${now
          .getHours()
          .toString()
          .padStart(2, '0')}-${now
          .getMinutes()
          .toString()
          .padStart(2, '0')}-${now
          .getSeconds()
          .toString()
          .padStart(2, '0')}`;

        const filename = `img_${formattedDate}.${blob.type.split('/')[1]}`;
        const file = new File([blob], filename, { type: blob.type });

        return file;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao converter a URL para arquivo:', error);
      }
    },
    async onSendProduct(data) {
      const currentUrlImage = /https?:\/\/\S+\.(jpg|jpeg|png|gif|heic|heif)(\?\S*)?/gi;

      const novaString =
        typeof data === 'string' ? data.replace(currentUrlImage, ' ') : '';
      const imageUrl =
        typeof data === 'string' ? data.match(currentUrlImage) : null;

      const payload = {
        conversationId: this.currentChat.id,
        message: novaString,
        templateParams: {},
        private: false,
        sender: this.sender,
        reply_id: this.toBeReplied.external_id || null,
      };

      if (imageUrl) {
        payload.url = imageUrl;
        payload.type = 'image';
      }

      this.sendMessage(payload);
    },
    tagConversation(value) {
      this.labelToInsert = this.labelList.find(item => item?.id === value);
    },
    addMedia(value) {
      if (!value) return;
      this.urlToBlob(value);
    },
    replaceText(message) {
      message = this.replaceNewTags(message);
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    replaceNewTags(message) {
      if (
        !message.includes('{{greeting}') ||
        !message.includes('{{protocol}')
      ) {
        return message;
      }
      const greeting = this.greetingFormat() || '';
      const protocolId = this.currentChat?.protocol_id || '';

      const replaceTags = message
        .replace('{{greeting}}', greeting)
        .replace('{{protocol}}', protocolId);
      return replaceTags;
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      const { can_reply: canReply } = this.currentChat;
      if (canReply || this.isAWhatsAppChannel) this.replyType = mode;
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertEmoji(emoji, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        emoji +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    emojiOnClick(emoji) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = emoji;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertEmoji(emoji, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      this.attachedFiles = [];
      this.attachedLocation = undefined;
      this.attachedContact = undefined;
      this.isRecordingAudio = false;
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.clearMessage();
        this.clearEmailField();
      }
    },
    toggleLocation(location) {
      this.attachedLocation = location;
    },
    toggleContact(contact) {
      const { name, phone_number } = contact;
      this.attachedContact = { name, phone: phone_number };
    },
    toggleAudioRecorderPlayPause() {
      if (this.isRecordingAudio) {
        if (!this.isRecorderAudioStopped) {
          this.isRecorderAudioStopped = true;
          this.$refs.audioRecorderInput.stopAudioRecording();
        } else if (this.isRecorderAudioStopped) {
          this.$refs.audioRecorderInput.playPause();
        }
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    hideMentions() {
      this.showMentions = false;
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onStateProgressRecorderChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onStateRecorderChanged(state) {
      this.recordingAudioState = state;
      if (state && 'notallowederror'.includes(state)) {
        this.toggleAudioRecorder();
      }
    },
    onFinishRecorder(file) {
      return file && this.onFileUpload(file);
    },
    async urlToBlob(url) {
      this.isUploading = true;
      try {
        const urlParts = url.split('/');
        const originalFileName = urlParts[urlParts.length - 1];
        const extension = originalFileName.split('.').pop() || 'bin';
        const firstName = originalFileName.split('.')[0];

        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        const contentType =
          response.headers.get('content-type') || 'application/octet-stream';

        const blob = new Blob([buffer], { type: contentType });
        const fileName = `${firstName}.${extension}`;
        const file = new File([blob], fileName, { type: contentType });
        this.attachFile({ file, url, contentType, isCanned: true });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.isUploading = false;
      }
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    onFileUpload(file) {
      this.isUploading = true;
      // if (this.globalConfig.directUploadsEnabled) {
      this.onDirectFileUpload(file);
      // } else {
      //   this.onIndirectFileUpload(file);
      // }
    },

    async onDirectFileUpload(es) {
      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.isATwilioSMSChannel
        ? MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL
        : MAXIMUM_FILE_UPLOAD_SIZE;

      if (!es) return;

      let file = es.file;
      if (
        ['image/heic', 'image/heif'].includes(file.type) ||
        (file.name && file.name.match(/\.(heic|heif)$/i))
      ) {
        try {
          const convertedBlob = await heicTo({
            blob: file,
            type: 'image/jpeg',
            quality: 0.5,
          });

          const newFile = new File(
            [convertedBlob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            {
              type: 'image/jpeg',
              lastModified: file.lastModified,
            }
          );

          es = { ...es, file: newFile };
          file = newFile;
        } catch (conversionError) {
          this.showAlert(this.$t('CONVERSATION.FILE_CONVERSION_FAILED'));
          return;
        }
      }

      if (checkFileSizeLimit(es, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        const uploadUrl = buildConversationDirectUploadUrl(
          this.accountId,
          this.currentChat.id
        );
        const upload = new DirectUpload(file, uploadUrl, {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
        });

        upload.create((error, blob) => {
          if (error) {
            this.showAlert(error);
            this.isUploading = false;
          } else {
            const fileUrl = buildBlobUrl(blob.signed_id, blob.filename);
            const file_type = file.type.split('/')[0];
            const type = [
              'image',
              'audio',
              'video',
              'location',
              'contact',
            ].includes(file_type)
              ? file_type
              : 'document';
            this.attachFile({ file: es, url: fileUrl, type });
            this.isUploading = false;
          }
        });
      } else {
        this.isUploading = false;
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    onIndirectFileUpload(file) {
      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.isATwilioSMSChannel
        ? MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL
        : MAXIMUM_FILE_UPLOAD_SIZE;
      if (!file) {
        return;
      }
      if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        this.attachFile({ file });
      } else {
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    attachFile({ blob, file, url, type, isCanned = false }) {
      const reader = new FileReader();
      if (isCanned) {
        reader.readAsDataURL(file);
      } else {
        reader.readAsDataURL(file.file);
      }
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          url,
          type,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },

    cleanAttachedFiles() {
      this.attachedFiles = [];
    },

    removeAttachmentLocation() {
      this.attachedLocation = undefined;
    },
    getMessagePayloadForWhatsapp(message) {
      const multipleMessagePayload = [];
      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          const attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile || attachment?.resource],
            private: false,
            message: caption,
            sender: this.sender,
            reply_id: this.toBeReplied.external_id
              ? this.toBeReplied.external_id
              : null,

            url: attachment.url,
            type: attachment.type,
          };
          multipleMessagePayload.push(attachmentPayload);
          caption = '';
        });
      } else if (this.attachedLocation) {
        let caption = message;
        const attachmentLocation = {
          conversationId: this.currentChat.id,
          location: this.attachedLocation,
          private: false,
          message: caption,
          sender: this.sender,
          reply_id: this.toBeReplied.external_id
            ? this.toBeReplied.external_id
            : null,
        };

        multipleMessagePayload.push(attachmentLocation);
        caption = '';
      } else if (this.attachedContact) {
        let caption = message;
        const attachmentLocation = {
          conversationId: this.currentChat.id,
          contact: this.attachedContact,
          private: false,
          message: caption,
          sender: this.sender,
          reply_id: this.toBeReplied.external_id
            ? this.toBeReplied.external_id
            : null,
        };

        multipleMessagePayload.push(attachmentLocation);
        caption = '';
      } else {
        const messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
          reply_id: this.toBeReplied.external_id
            ? this.toBeReplied.external_id
            : null,
        };
        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
        reply_id: this.toBeReplied.external_id
          ? this.toBeReplied.external_id
          : null,
      };

      if (this.inReplyTo) {
        messagePayload.contentAttributes = { in_reply_to: this.inReplyTo };
      }

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            const file = attachment.resource?.file
              ? attachment.resource.file
              : attachment.resource;
            messagePayload.files.push(file);
            messagePayload.url = attachment.url;
            messagePayload.type = attachment.type;
          }
        });
      }

      if (this.attachedLocation) {
        messagePayload.location = this.attachedLocation;
      }

      if (this.attachedContact) {
        messagePayload.contact = this.attachedContact;
      }
      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCEmailFromLastChat() {
      if (this.lastEmail) {
        const {
          content_attributes: { email: emailAttributes = {} },
        } = this.lastEmail;
        const cc = emailAttributes.cc || [];
        const bcc = emailAttributes.bcc || [];
        this.ccEmails = cc.join(', ');
        this.bccEmails = bcc.join(', ');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.send-button {
  margin-bottom: 0;
}

.message-signature-wrap {
  margin: 0 var(--space-normal);
  padding: var(--space-small);
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border: 1px dashed var(--s-100);
  border-radius: var(--border-radius-small);
  max-height: 8vh;
  overflow: auto;

  &:hover {
    background: var(--s-25);
  }
}

.message-signature {
  width: fit-content;
  margin: 0;
}

.attachment-preview-box {
  padding: 0 var(--space-normal);
  background: transparent;
}

.reply-box {
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  box-shadow: 0 -2px 8px rgba(0, 33, 54, 0.03);
  padding: 0 var(--space-normal) var(--space-normal);
  // Nota: o retângulo EXTERNO fica branco; só a caixa interna (.composer-box) fica âmbar
}

// Caixa arredondada do composer (igual ao .comp-box do protótipo)
.composer-box {
  border: 1.5px solid var(--s-300);
  border-radius: 12px;
  // overflow: visible para não cortar o dropdown de menção (@) que abre pra cima.
  // O conteúdo interno tem padding, então não vaza dos cantos arredondados.
  overflow: visible;
  background: var(--color-surface);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus-within {
    border-color: var(--color-woot);
    box-shadow: 0 0 0 3px rgba(1, 198, 216, 0.15);
  }
}

.reply-box.is-private .composer-box {
  background: #fffdf5;
  border-color: #f0e2b8;
}
// No dark o creme (#fffdf5) + a menubar escura viram "amarelo com faixas
// pretas". Vira um bloco âmbar escuro coeso: tinta sutil no composer e a
// menubar transparente (sem faixa no topo). US-4328
:root[data-theme='dark'] .reply-box.is-private .composer-box {
  background: rgba(230, 192, 12, 0.08);
  border-color: rgba(230, 192, 12, 0.32);
}
:root[data-theme='dark'] .reply-box.is-private ::v-deep .ProseMirror-menubar {
  background: transparent !important;
}
.send-button {
  margin-bottom: 0;
}

.reply-box__top {
  position: relative;
  padding: var(--space-small) var(--space-normal);
  margin-top: 0;
  // Altura consistente entre Responder (textarea) e Nota (editor rico com menubar)
  min-height: 9.4rem;
  display: flex;
  flex-direction: column;

  ::v-deep .input,
  ::v-deep .editor-root {
    flex: 1;
  }

  // O editor da Nota tem um menubar extra; reduz o min do ProseMirror p/ igualar a altura
  ::v-deep .ProseMirror {
    min-height: 2.2rem;
  }
}

.emoji-dialog {
  top: unset;
  bottom: -40px;
  left: -320px;
  right: unset;

  &::before {
    right: var(--space-minus-normal);
    bottom: var(--space-small);
    transform: rotate(270deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
  }
}

.replied-message {
  width: fit-content;
  border-radius: 10px;
  border-left: 4px solid #00c7d8;
  margin: 4px;
  padding: 4px 12px 4px 12px;
  background-color: #0c3a67;
  color: #fff;
}

.replied-message__media {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  margin: var(--space-micro) 0;
}

.replied-message__thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: var(--border-radius-normal);
}

.replied-message__media-label {
  display: flex;
  align-items: center;
  gap: var(--space-micro);
}

.replyContainer {
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.cancel-button {
  cursor: pointer;
  color: var(--b-400);
  margin-right: 10px;
}

.title {
  color: #01c7d5;
  font-weight: bold;
}

.emoji-dialog--rtl {
  left: unset;
  right: -320px;
  &::before {
    left: var(--space-minus-normal);
    transform: rotate(90deg);
    right: 0;
    bottom: var(--space-small);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
  }
}

.emoji-dialog--expanded {
  left: unset;
  bottom: var(--space-jumbo);
  position: absolute;
  z-index: var(--z-index-normal);

  &::before {
    transform: rotate(0deg);
    left: var(--space-smaller);
    bottom: var(--space-minus-small);
  }
}
.message-signature {
  margin-bottom: 0;

  ::v-deep p:last-child {
    margin-bottom: 0;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 4px solid #ccc;
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
